/**
 * OrgChartMemberPanel 컴포넌트 (V55.14)
 *
 * 조직도에서 구성원 이름 클릭 시 우측에 표시되는 상세 정보 사이드패널
 *
 * **기능:**
 * - 구성원 상세 정보 표시 (읽기 전용)
 * - X 버튼으로 닫기
 * - 프로필 사진 표시 (ReadOnlyEmployeePhoto 스타일)
 *
 * **표시 필드:**
 * - 프로필 사진
 * - 이름 (nameKo)
 * - 소속 (orgUnitName)
 * - 직책 (jobTitle)
 * - 직위 (positionTitle)
 * - 좌석 (seatCode / seatLabel)
 * - 전화번호 (phoneNumber)
 * - 이메일 (email)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, AlertCircle, User, Building2, Briefcase, MapPin, Phone, Mail } from 'lucide-react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/config';
import { useOrgChartMemberDetail, OrgChartMemberDetailData } from '../../../shared/hooks/useOrgChartMemberDetail';

// V64: 근무 상태 스타일 매핑
const WORK_STATUS_STYLES: Record<string, { color: string; background: string }> = {
  '출근': { color: '#16a34a', background: '#dcfce7' },
  '미출근': { color: '#9ca3af', background: '#f3f4f6' },
  '퇴근': { color: '#6b7280', background: '#f3f4f6' },
  '휴가': { color: '#7c3aed', background: '#f3e8ff' },
  '오전반차': { color: '#0284c7', background: '#e0f2fe' },
  '오후반차': { color: '#0284c7', background: '#e0f2fe' },
  '퍼스널힐링데이': { color: '#7c3aed', background: '#f3e8ff' },
  // V66: 휴직중 — 옅은 주황
  '휴직중': { color: '#c2410c', background: '#fff7ed' },
};

// 사진 영역 크기 (3:4 비율) - OrgMemberProfileModal과 동일
const PHOTO_WIDTH = 100;
const PHOTO_HEIGHT = 133;

/**
 * Props
 */
export interface OrgChartMemberPanelProps {
  isOpen: boolean;
  targetUserId: string | null;
  onClose: () => void;
}

/**
 * 읽기 전용 프로필 사진 컴포넌트
 */
function ReadOnlyEmployeePhoto({ photoPath, employeeName }: { photoPath: string; employeeName: string }) {
  const [resolvedURL, setResolvedURL] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!photoPath || !storage) {
      setResolvedURL(undefined);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const storageRef = ref(storage, photoPath);
    getDownloadURL(storageRef)
      .then((url) => {
        setResolvedURL(url);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('[ReadOnlyEmployeePhoto] getDownloadURL 실패:', error);
        setResolvedURL(undefined);
        setIsLoading(false);
        setHasError(true);
      });
  }, [photoPath]);

  const containerStyle: React.CSSProperties = {
    width: `${PHOTO_WIDTH}px`,
    height: `${PHOTO_HEIGHT}px`,
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    flexShrink: 0,
    position: 'relative',
  };

  const photoStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const placeholderStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f5f9',
  };

  const loadingStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.8)',
  };

  return (
    <div style={containerStyle}>
      {resolvedURL && !hasError ? (
        <img
          src={resolvedURL}
          alt={`${employeeName} 프로필 사진`}
          style={photoStyle}
          onError={() => setHasError(true)}
        />
      ) : (
        <div style={placeholderStyle}>
          <User size={40} color="#94a3b8" />
        </div>
      )}
      {isLoading && (
        <div style={loadingStyle}>
          <Loader2 size={20} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      )}
    </div>
  );
}

/**
 * 정보 행 컴포넌트
 */
function InfoRow({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  if (!value) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 0',
      borderBottom: '1px solid #f1f5f9',
    }}>
      <Icon size={16} color="#64748b" style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>
          {label}
        </div>
        <div style={{
          fontSize: '13px',
          color: '#1e293b',
          wordBreak: 'break-word',
        }}>
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * 패널 내용 컴포넌트
 */
function PanelContent({ member }: { member: OrgChartMemberDetailData }) {
  return (
    <div style={{ padding: '16px' }}>
      {/* 상단: 사진 + 이름/소속 */}
      <div style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <ReadOnlyEmployeePhoto
          photoPath={member.photoPath}
          employeeName={member.nameKo || '직원'}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            // V66: 휴직중 — 옅은 주황 (#c2410c)
            color: member.currentWorkStatus === '휴직중' ? '#c2410c' : '#1e293b',
            margin: '0 0 4px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span>{member.nameKo || '-'}</span>
            {/* V66: 휴직중 배지 (currentWorkStatus 가 BE 에서 '휴직중' 으로 강제 설정됨) */}
            {member.currentWorkStatus === '휴직중' && (
              <span style={{
                padding: '2px 10px', borderRadius: 10,
                background: '#fff7ed', color: '#c2410c',
                border: '1px solid #fed7aa',
                fontSize: 12, fontWeight: 600,
              }}>
                휴직중
              </span>
            )}
          </h3>
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            marginBottom: '8px',
          }}>
            {member.orgUnitName || '-'}
          </div>
          {/* 직책/직위 배지 */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {member.jobTitle && (
              <span style={{
                fontSize: '11px',
                color: '#3b82f6',
                background: '#eff6ff',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 500,
              }}>
                {member.jobTitle}
              </span>
            )}
            {member.positionTitle && (
              <span style={{
                fontSize: '11px',
                color: '#64748b',
                background: '#f1f5f9',
                padding: '2px 8px',
                borderRadius: '4px',
              }}>
                {member.positionTitle}
              </span>
            )}
          </div>
          {/* V64: 현재 근무 상태 */}
          {member.currentWorkStatus && (
            <div style={{ marginTop: '8px' }}>
              <span style={{
                fontSize: '11px',
                color: WORK_STATUS_STYLES[member.currentWorkStatus]?.color || '#6b7280',
                background: WORK_STATUS_STYLES[member.currentWorkStatus]?.background || '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 500,
              }}>
                {member.currentWorkStatus}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 상세 정보 - V55.14.3: 좌석/전화/이메일 항상 표시 (미지정 시 '-') */}
      <div>
        <InfoRow icon={MapPin} label="좌석" value={member.seatLabel || '-'} />
        <InfoRow icon={Phone} label="전화번호" value={member.phoneNumber || '-'} />
        <InfoRow icon={Mail} label="이메일" value={member.email || '-'} />
      </div>
    </div>
  );
}

/**
 * OrgChartMemberPanel 컴포넌트
 */
export function OrgChartMemberPanel({
  isOpen,
  targetUserId,
  onClose,
}: OrgChartMemberPanelProps) {
  const { member, isLoading, error, fetchMember, reset } = useOrgChartMemberDetail();

  // Panel 오픈 시 데이터 fetch
  useEffect(() => {
    if (isOpen && targetUserId) {
      fetchMember(targetUserId);
    } else if (!isOpen) {
      reset();
    }
  }, [isOpen, targetUserId, fetchMember, reset]);

  if (!isOpen) return null;

  return (
    <>
      {/* 사이드 패널 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '320px',
          height: '100%',
          backgroundColor: 'white',
          borderLeft: '1px solid #e2e8f0',
          boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.08)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            background: '#fafafa',
          }}
        >
          <h4 style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            margin: 0,
          }}>
            구성원 정보
          </h4>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={18} color="#64748b" />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
        }}>
          {/* 로딩 */}
          {isLoading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 16px',
              gap: '12px',
            }}>
              <Loader2 size={28} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                정보를 불러오는 중...
              </span>
            </div>
          )}

          {/* 에러 */}
          {!isLoading && error && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 16px',
              gap: '12px',
            }}>
              <AlertCircle size={28} color="#ef4444" />
              <span style={{
                fontSize: '13px',
                color: '#64748b',
                textAlign: 'center',
              }}>
                {error}
              </span>
            </div>
          )}

          {/* 내용 */}
          {!isLoading && !error && member && (
            <PanelContent member={member} />
          )}
        </div>
      </div>

      {/* 스피너 애니메이션 */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
